import {Backdrop} from '@material-ui/core';
import React, {useCallback, useState} from 'react';
import {ImageStyle, Platform, TouchableOpacity, View} from 'react-native';
import {useSelector} from 'react-redux';
import {images} from '../../assets/icons';
import {contentSelector} from '../../redux/selectors/resources';
import {Accordion} from '../../shared/components/accordion';
import {Button} from '../../shared/components/button';
import {Drawer} from '../../shared/components/drawer';
import {Hr} from '../../shared/components/hr';
import {LoadableContent} from '../../shared/components/loadableContent';
import {Markdown} from '../../shared/components/markdown';

import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Text} from '../../shared/components/text';
import {NamedStyles, StyleHelper, useStyles} from '../../shared/hooks/styles';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {formatPhone, hexToRgbA, openExternalPhone} from '../../shared/pipes';
import {useTheme} from '../../shared/theme';
import {desktopMenuHeight, DeviceSize} from '../../shared/theme/helpers';
import {stylePatterns} from '../../shared/theme/patterns';
import {useGlobalScroll} from '../../shared/hooks/useGlobalScroll';
import {Chatbot} from './components/chatbot';
import {SupportServiceQuestionsStack} from './components/faq';

import {IStateProps} from './model';
import {FEATURE_FLAGS} from '../../shared/constants/featureFlags';

const phoneIconSize = 24;

export default ({onPressTopicsButton, onPressAssistantButton, questions, isFinancial}: IStateProps) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const content = useSelector(contentSelector());
  const {enableScroll, disableScroll} = useGlobalScroll();
  const [isChatbotVisible, setChatbotVisible] = useState(false);
  const styles = useStyles((helper) => createStyles(helper));
  const {Phone} = images;

  const renderPhoneBlock = (title: string, phone?: string, testIdPrefix?: string) => (
    <View testID={`${testIdPrefix}-container-id`} style={styles.phoneContainer}>
      <Text testID={`${testIdPrefix}-title-id`} style={styles.phoneTitle}>
        {title}:
      </Text>
      <TouchableOpacity
        testID={`${testIdPrefix}-button-id`}
        onPress={() => openExternalPhone(phone)}
        {...{dataSet: {class: 'link'}}}>
        <View testID={`${testIdPrefix}-phone-content-id`} style={styles.phoneContent}>
          <View style={styles.phoneContentIcon}>
            <Phone width={phoneIconSize} height={phoneIconSize} color={styles.phoneContentIcon.color} />
          </View>
          <Text testID={`${testIdPrefix}-phone-value-id`} style={styles.phoneContentText}>
            {formatPhone(phone)}
          </Text>
        </View>
      </TouchableOpacity>
    </View>
  );

  const phonesBlock = () => {
    return (
      <React.Fragment>
        {renderPhoneBlock('Горячая линия Единой социальной карты', content.esksoContacts.supportPhone, 'support-phone')}
        {isFinancial
          ? renderPhoneBlock('Горячая линия банка', content.services.bankingService.bankPhone, 'bank-phone')
          : null}
        {renderPhoneBlock(
          'Горячая линия транспортного оператора',
          content.services.transportService.nonFinancial.eCardSupportPhone,
          'transport-operator-phone',
        )}
        {renderPhoneBlock('Единый социальный телефон', content.esksoContacts.socialPhone, 'social-phone')}
      </React.Fragment>
    );
  };

  const handleOpenDrawer = useCallback(() => {
    setChatbotVisible(true);
    disableScroll();
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setChatbotVisible(false);
    enableScroll();
  }, []);

  return (
    <Screen style={{position: 'relative'}}>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <View>
          <View style={styles.container}>
            <View style={styles.wrap}>
              <View style={styles.leftContainer}>
                {isDesktop ? (
                  <Text testID='suppoty-service-title-id' style={styles.sectionTitle}>
                    Служба поддержки
                  </Text>
                ) : null}
                <View style={styles.leftContainerTextWrapper}>
                  <Markdown>
                    {isDesktop ? content.support.descriptionDesktopMarkdown : content.support.descriptionMobileMarkdown}
                  </Markdown>
                </View>
              </View>
              <View style={styles.rightContainer}>
                {isDesktop ? (
                  <>
                    <View style={styles.cardButtonWrap}>
                      {FEATURE_FLAGS.supportServiceTopics && (
                        <Button
                          id='support-goto-topics'
                          style={[styles.cardButton, {marginRight: 20}]}
                          type='secondary'
                          onPress={onPressTopicsButton}
                          text='Мои обращения'
                        />
                      )}
                      <Button
                        id='support-goto-topics'
                        style={[styles.cardButton, {marginLeft: 'auto'}]}
                        onPress={handleOpenDrawer}
                        text='Уралочка Ассистент'
                      />
                    </View>
                    <View style={styles.cardWrap}>
                      <View style={styles.cardContent}>{phonesBlock()}</View>
                      <View style={styles.cardTraceryContainer}>
                        <images.ServiceDetailsPattern
                          {...styles.cardTraceryImage}
                          color={
                            Platform.OS === 'web' ? hexToRgbA(theme.color.shaddowBg, 0.15) : theme.color.patternBgColor
                          }
                          width={styles.sectionContainerBgImage.width}
                          height={styles.sectionContainerBgImage.height}
                          hideForVisuallyImpaired
                        />
                      </View>
                    </View>
                  </>
                ) : (
                  <>
                    <View style={styles.cardButtonWrap}>
                      {FEATURE_FLAGS.supportServiceTopics && (
                        <Button
                          id='support-goto-topics'
                          style={[styles.cardButton, {marginRight: 20}]}
                          type='secondary'
                          onPress={onPressTopicsButton}
                          text='Мои обращения'
                        />
                      )}
                      <Button
                        id='support-goto-topics'
                        style={styles.cardButton}
                        onPress={onPressAssistantButton}
                        text='Уралочка Ассистент'
                      />
                    </View>
                    {phonesBlock()}
                  </>
                )}
              </View>
              {!isDesktop && (
                <View style={styles.sectionContainerBg}>
                  <images.ServiceDetailsPattern
                    {...styles.sectionContainerBgImage}
                    color={Platform.OS === 'web' ? hexToRgbA(theme.color.shaddowBg, 0.15) : theme.color.patternBgColor}
                    width={styles.sectionContainerBgImage.width}
                    height={styles.sectionContainerBgImage.height}
                    hideForVisuallyImpaired
                  />
                </View>
              )}
            </View>
            {!isDesktop && <Hr marginTop={0} marginBottom={10} />}
            <View style={styles.questionsWrap}>
              <LoadableContent loading={!questions}>
                {questions?.length ? (
                  <Text testID='reference-info-id' style={styles.questionsTitle}>
                    Справочная информация
                  </Text>
                ) : null}
                {questions
                  ? questions.map((data, index) => (
                      <Accordion
                        index={index}
                        title={data.name}
                        key={`${data.name}${index}`}
                        contentCustomStyle={styles.questionsAccordionContent}>
                        <SupportServiceQuestionsStack questions={data.questions || []} />
                      </Accordion>
                    ))
                  : null}
              </LoadableContent>
            </View>
          </View>
        </View>
      </CustomScrollView>
      <View style={styles.drawerContainer}>
        {isDesktop && <Backdrop open={isChatbotVisible} onClick={handleCloseDrawer} />}
        {isChatbotVisible && (
          <View style={styles.drawerWrapper}>
            <Drawer open={isChatbotVisible} onClose={handleCloseDrawer} variant='persistent' keepMounted>
              <View style={styles.drawerInnerWrapper}>
                <Chatbot
                  header={
                    <View style={styles.chatbotHeader}>
                      <Text style={styles.chatbotTitle}>Уралочка Ассистент</Text>
                      <TouchableOpacity onPress={handleCloseDrawer} style={styles.drawerCloseButton}>
                        <images.Close width={24} height={24} />
                      </TouchableOpacity>
                    </View>
                  }
                />
              </View>
            </Drawer>
          </View>
        )}
      </View>
    </Screen>
  );
};

const desktopCardWidth = 548;

const createStyles = ({theme, isDesktop, mediaOnly, mediaExclude}: StyleHelper): NamedStyles<any> => ({
  container: {
    ...mediaOnly(DeviceSize.desktop, {marginTop: 32, paddingHorizontal: 24}, {marginTop: 28, paddingHorizontal: 16}),
    paddingBottom: theme.screen.bottomOffset - (isDesktop ? 30 : 20),
  },
  sectionTitle: {
    ...theme.fonts.h2,
    marginBottom: 40,
  },
  wrap: {
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
  },
  leftContainer: {
    ...mediaOnly(DeviceSize.desktop, {flex: 1}, {width: '100%'}),
    paddingRight: isDesktop ? 28 : 0,
  },
  leftContainerTextWrapper: {
    marginBottom: isDesktop ? 30 : 20,
  },
  rightContainer: mediaOnly(
    DeviceSize.desktop,
    {width: desktopCardWidth, flexBasis: desktopCardWidth},
    {width: '100%'},
  ),
  cardWrap: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: isDesktop ? 218 : 170,
    borderRadius: 14,
    marginBottom: 30,
    width: isDesktop ? desktopCardWidth : '100%',
    backgroundColor: theme.supportService.cardBgColor,
    ...theme.supportService.cardBoxShadow,
    flexDirection: 'row',
  },
  cardTraceryContainer: {
    position: 'absolute',
    width: 466,
    height: 479,
    right: -232,
    bottom: -142,
  },
  cardTraceryImage: {
    marginLeft: 'auto',
    marginBottom: 'auto',
    alignSelf: 'flex-start',
    width: 466,
    height: 479,
    resizeMode: 'contain',
    color: theme.supportService.cardTraceryColor,
  } as ImageStyle,
  cardContent: {
    paddingTop: 30,
    paddingHorizontal: 40,
    paddingBottom: 6,
    maxWidth: '100%',
    width: '100%',
    flexGrow: 1,
    alignSelf: 'center',
  },
  cardTitle: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h6, theme.fonts.bodySmall),
    marginBottom: 16,
  },
  cardPhoneLink: {
    marginBottom: 20,
  },
  cardPhone: {
    ...stylePatterns.flexCenterVertical,
    width: 240,
  },
  cardPhoneIcon: {
    color: theme.supportService.phoneColor,
  },
  cardPhoneText: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h4, theme.fonts.action),
    marginLeft: 13,
  },
  cardButtonWrap: {
    display: 'flex',
    ...mediaOnly(DeviceSize.mobile, {flexDirection: 'column'}, {flexDirection: 'row'}),
    alignSelf: isDesktop ? 'flex-start' : undefined,
    width: isDesktop ? desktopCardWidth : undefined,
    marginBottom: 30,
  },
  cardButton: {
    paddingVertical: 8,
    marginHorizontal: 0,
    ...mediaOnly(DeviceSize.desktop, {maxWidth: 264, width: '100%'}, {flex: 1, width: '100%'}),
  },
  drawerContainer: {
    height: '100%',
    flex: 1,
    position: 'absolute',
    zIndex: 1,
  },
  drawerWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  drawerCloseButton: {
    position: 'absolute',
    top: 18,
    right: 20,
  },
  drawerInnerWrapper: {
    overflow: 'hidden',
    ...mediaOnly(
      DeviceSize.mobile,
      {
        width: 320,
        height: '100%',
      },
      {
        width: 608,
        height: `calc(100vh - ${desktopMenuHeight}px)` as any,
      },
    ),
  },
  chatbotDrawer: {
    minWidth: 600,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  chatbotHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 18,
    backgroundColor: 'transparent',
    shadowColor: '#252C292E',
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowRadius: 18,
    elevation: 4,
    paddingHorizontal: 20,
    zIndex: 1,
  },
  chatbotTitle: {
    marginHorizontal: 'auto',
    ...theme.fonts.h6,
  },
  phoneContainer: {
    paddingBottom: 24,
    ...mediaOnly(DeviceSize.mobile, {
      display: 'flex',
      flexDirection: 'column',
    }),
  },
  phoneTitle: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h5, theme.fonts.action),
    marginBottom: isDesktop ? 12 : 14,
  },
  phoneContent: {
    ...stylePatterns.flexCenterVertical,
    ...mediaExclude(DeviceSize.desktop, {paddingLeft: 25}),
  },
  phoneContentIcon: {
    color: theme.supportService.phoneColor,
  },
  phoneContentText: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h3Light, theme.fonts.h6Light),
    paddingLeft: 13,
  },
  questionsWrap: {
    marginTop: 20,
  },
  questionsTitle: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h3, theme.fonts.h6),
    paddingBottom: isDesktop ? 40 : 22,
  },
  questionsAccordionContent: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  sectionContainerBg: {
    position: 'absolute',
    zIndex: -1,
    width: 466,
    height: 479,
    right: -330,
    bottom: -124,
  },
  sectionContainerBgImage: {
    marginLeft: 'auto',
    marginBottom: 'auto',
    alignSelf: 'flex-start',
    color: theme.color.shaddowBg,
    width: 466,
    height: 479,
    resizeMode: 'contain',
  } as ImageStyle,
});
